import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  UserCheck, RefreshCw, Loader2, Check, X, Mail, Clock,
  Inbox, ShieldCheck, AlertCircle, Trash2,
} from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';
import { EmailOutboxPanel } from './EmailOutboxPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_TABS = [
  { id: 'pending', label: 'Pending', icon: Clock, color: '#ff9e00' },
  { id: 'active', label: 'Active Users', icon: ShieldCheck, color: '#39ff14' },
  { id: 'rejected', label: 'Rejected', icon: X, color: '#ef4444' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export const RegistrationApprovalView = () => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async (status) => {
    setError('');
    try {
      const res = await axios.get(`${API}/admin/registrations`, { params: { status } });
      setItems(res.data.items || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { setLoading(true); load(tab); }, [tab, load]);

  const handleRefresh = () => { setRefreshing(true); load(tab); };

  const act = async (userId, action) => {
    setActingId(userId);
    setError('');
    try {
      await axios.post(`${API}/admin/registrations/${userId}/${action}`);
      setToast(action === 'approve' ? 'User approved — confirmation email queued.' : 'Registration rejected — notice email queued.');
      setTimeout(() => setToast(''), 2400);
      await load(tab);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = async (user) => {
    const confirm = window.confirm(
      `Permanently delete ${user.email}?\n\nThis removes the user account AND all of their guestbook entries. This cannot be undone.`
    );
    if (!confirm) return;
    setActingId(user.id);
    setError('');
    try {
      const res = await axios.delete(`${API}/admin/users/${user.id}`);
      const removed = res.data?.cascade?.guestbook_entries_removed ?? 0;
      setToast(`Deleted ${user.email}${removed ? ` (+${removed} guestbook entries)` : ''}.`);
      setTimeout(() => setToast(''), 2600);
      await load(tab);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setActingId(null);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8" data-testid="registration-approval-view">
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#ff9e00]/10 border border-[#ff9e00]/25 rounded-full">
            <UserCheck className="w-3 h-3 text-[#ff9e00] shrink-0" />
            <span className="font-mono text-[10px] text-[#ff9e00] tracking-widest font-semibold uppercase">06 // REGISTRATION GATE</span>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight uppercase">
            Registration <span className="text-[#ff9e00]">Approval</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed max-w-2xl">
            Review and authorize new operator registrations. Approval and rejection notices are dispatched from{' '}
            <span className="font-mono text-[#00f0ff]">no-reply@rishabsalgotra.com</span> (replies are not monitored).
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-4 py-2 rounded border border-[#ff9e00]/40 bg-[#ff9e00]/10 text-[#ff9e00] hover:bg-[#ff9e00]/20 disabled:opacity-50 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
          data-testid="btn-approval-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </section>

      {toast && (
        <div className="glass-card border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-4 py-3 rounded text-xs font-mono flex items-center gap-2" data-testid="approval-toast">
          <ShieldCheck className="w-3.5 h-3.5" /> {toast}
        </div>
      )}
      {error && (
        <div className="glass-card border border-rose-500/40 bg-rose-500/10 text-rose-300 px-4 py-3 rounded text-xs font-mono flex items-start gap-2" data-testid="approval-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 p-1 rounded-lg w-fit">
        {STATUS_TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer ${active ? 'text-slate-950' : 'text-slate-400 hover:text-slate-200'}`}
              style={active ? { background: t.color } : {}}
              data-testid={`approval-tab-${t.id}`}
            >
              <Icon className="w-3 h-3" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      <div className="glass-card border border-slate-800 rounded-xl overflow-hidden" data-testid="approval-table-wrap">
        {loading ? (
          <div className="px-6 py-16 flex items-center justify-center text-slate-500 font-mono text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading registrations…
          </div>
        ) : items.length === 0 ? (
          <div className="px-6 py-16 flex flex-col items-center justify-center gap-3 text-slate-500 font-mono text-xs" data-testid="approval-empty">
            <Inbox className="w-6 h-6" />
            <span>No {tab} registrations.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="approval-table">
              <thead className="bg-slate-900/60 border-b border-slate-800">
                <tr className="text-left">
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Operator</th>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Email</th>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">Role</th>
                  <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">{tab === 'active' ? 'Approved' : 'Submitted'}</th>
                  {(tab === 'pending' || tab === 'active') && (
                    <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-slate-400 text-right">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} className="border-b border-slate-900 hover:bg-slate-900/30 transition-colors" data-testid={`approval-row-${u.id}`}>
                    <td className="px-4 py-3 text-slate-200 font-medium">{u.full_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-[#00f0ff] flex items-center gap-1.5">
                        <Mail className="w-3 h-3" /> {u.email}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${u.role === 'admin' ? 'border-[#ff9e00]/60 bg-[#ff9e00]/10 text-[#ff9e00]' : 'border-slate-700 bg-slate-900 text-slate-300'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-[10px] text-slate-500">
                      {tab === 'active' ? formatDate(u.approved_at || u.created_at) : formatDate(u.created_at)}
                    </td>
                    {tab === 'pending' && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => act(u.id, 'approve')}
                            disabled={actingId === u.id}
                            className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500 hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
                            data-testid={`btn-approve-${u.id}`}
                          >
                            {actingId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            <span>Approve</span>
                          </button>
                          <button
                            onClick={() => act(u.id, 'reject')}
                            disabled={actingId === u.id}
                            className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500 hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
                            data-testid={`btn-reject-${u.id}`}
                          >
                            <X className="w-3 h-3" />
                            <span>Reject</span>
                          </button>
                        </div>
                      </td>
                    )}
                    {tab === 'active' && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {u.role === 'admin' ? (
                            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600 italic" data-testid={`admin-protected-${u.id}`}>
                              protected
                            </span>
                          ) : (
                            <button
                              onClick={() => handleDelete(u)}
                              disabled={actingId === u.id}
                              className="px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500 hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
                              data-testid={`btn-delete-${u.id}`}
                            >
                              {actingId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EmailOutboxPanel />
    </motion.div>
  );
};
